

// client/src/pages/invoice/ProcessingPage.jsx

import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { processInvoices } from "../../api/invoiceApi";

const STEPS = [
  { label: "Reading PDF structure",          hint: "Parsing document layout and page content" },
  { label: "Extracting invoice intelligence", hint: "Identifying vendor, dates, amounts and line items" },
  { label: "Calculating GST",                hint: "Computing tax breakdowns across all invoices" },
  { label: "Generating Excel output",        hint: "Building structured output sheet for download" },
];

export default function ProcessingPage() {

  const navigate = useNavigate();
  const location = useLocation();

  const files = useMemo(() => location.state?.files || [], [location.state]);

  const [progress, setProgress]   = useState(0);
  const [step, setStep]           = useState(0);
  const [scrolled, setScrolled]   = useState(false);
  const [hidden, setHidden]       = useState(false);
  const lastY                     = useRef(0);
  const backendResult             = useRef(null);
  const animationFinished         = useRef(false);
  const requestStarted            = useRef(false);

  // ── HEADER SCROLL ──
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      if (y > lastY.current && y > 120) setHidden(true);
      else setHidden(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── PROCESSING LOGIC (unchanged) ──
  useEffect(() => {
    if (!files.length) { navigate("/invoice", { replace: true }); return; }
    if (requestStarted.current) return;
    requestStarted.current = true;

    processInvoices(files)
      .then((res) => {
        backendResult.current = res;
        if (animationFinished.current) {
          navigate("/invoice/completed", { state: { result: res }, replace: true });
        }
      })
      .catch((err) => console.error("❌ Processing Failed:", err));

    let current = 0;
    const interval = setInterval(() => {
      current++;
      setProgress(current);
      if (current >= 25) setStep(1);
      if (current >= 50) setStep(2);
      if (current >= 75) setStep(3);
      if (current >= 100) {
        setStep(4);
        clearInterval(interval);
        animationFinished.current = true;
        if (backendResult.current) {
          navigate("/invoice/completed", { state: { result: backendResult.current }, replace: true });
        }
      }
    }, 60);

    return () => clearInterval(interval);
  }, [files, navigate]);

  return (
    <div style={styles.page}>

      {/* ── HEADER ── */}
      <header style={{
        ...styles.header,
        ...(scrolled ? styles.headerScrolled : {}),
        ...(hidden   ? styles.headerHidden   : {}),
      }}>
        <div style={styles.headerInner}>
          <div style={styles.brand}>
            <div style={styles.logoWrap}>📄</div>
            <div>
              <div style={styles.brandTitle}>SISL Invoice Processor</div>
              <div style={styles.brandSubtitle}>Raw PDF Analyzer</div>
            </div>
          </div>

          <div style={styles.statusPill}>
            <span style={styles.statusDot} />
            <span style={styles.statusText}>Processing</span>
          </div>
        </div>
      </header>

      {/* ── MAIN ── */}
      <main style={styles.main}>

        {/* PAGE HEADER */}
        <div style={styles.pageHeader}>
          <div style={styles.eyebrowRow}>
            <span style={styles.eyebrowDot} />
            <span style={styles.eyebrow}>Invoice</span>
            <span style={styles.eyebrowSep}>/</span>
            <span style={styles.eyebrowMuted}>Processing</span>
          </div>
          <h1 style={styles.pageTitle}>Processing Your Invoices</h1>
          <p style={styles.pageSubtitle}>
            {files.length} file{files.length !== 1 ? "s" : ""} queued · Please keep this window open
          </p>
        </div>

        {/* CONTENT GRID */}
        <div style={styles.contentGrid}>

          {/* ── LEFT — PROGRESS CARD ── */}
          <div style={styles.card}>

            <div style={styles.cardHeader}>
              <div style={styles.iconWrap}>
                <span style={styles.cardIcon}>⚙️</span>
              </div>
              <div>
                <div style={styles.cardTitle}>Extraction in Progress</div>
                <div style={styles.cardSubtitle}>Do not close or refresh this page</div>
              </div>
            </div>

            <div style={styles.divider} />

            {/* PROGRESS BAR */}
            <div style={styles.progressSection}>
              <div style={styles.progressTopRow}>
                <span style={styles.progressLabel}>Overall Progress</span>
                <span style={styles.progressPct}>{progress}%</span>
              </div>
              <div style={styles.progressTrack}>
                <div style={{ ...styles.progressFill, width: `${progress}%` }} />
              </div>
            </div>

            <div style={styles.divider} />

            {/* STEP LIST */}
            <div style={styles.stepList}>
              {STEPS.map((s, i) => {
                const isDone    = step > i + 1;
                const isActive  = step === i + 1 || (i === 0 && step === 0);
                return (
                  <div key={i} style={styles.stepItem}>
                    {/* Step indicator */}
                    <div style={{
                      ...styles.stepIndicator,
                      ...(isDone   ? styles.stepIndicatorDone   : {}),
                      ...(isActive ? styles.stepIndicatorActive : {}),
                    }}>
                      {isDone ? (
                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                          <polyline points="2,6 5,9 10,3" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <span style={{
                          fontSize: "10px", fontWeight: "700",
                          color: isActive ? "#ffffff" : "#9ca3af",
                        }}>{i + 1}</span>
                      )}
                    </div>

                    {/* Step text */}
                    <div style={styles.stepContent}>
                      <div style={{
                        ...styles.stepTitle,
                        ...(isDone   ? styles.stepTitleDone   : {}),
                        ...(isActive ? styles.stepTitleActive : {}),
                      }}>
                        {s.label}
                        {isActive && <span style={styles.activeDots}><span>.</span><span>.</span><span>.</span></span>}
                      </div>
                      <div style={styles.stepHint}>{s.hint}</div>
                    </div>

                    {/* Done tick */}
                    {isDone && (
                      <span style={styles.doneTick}>✓</span>
                    )}
                  </div>
                );
              })}
            </div>

          </div>

          {/* ── RIGHT — FILES + STATUS CARD ── */}
          <div style={styles.infoCard}>

            <div style={styles.cardHeader}>
              <div style={{ ...styles.iconWrap, backgroundColor: "#f5f3ff" }}>
                <span style={styles.cardIcon}>📂</span>
              </div>
              <div>
                <div style={styles.cardTitle}>Files in Queue</div>
                <div style={styles.cardSubtitle}>{files.length} file{files.length !== 1 ? "s" : ""} being processed</div>
              </div>
            </div>

            <div style={styles.divider} />

            {/* FILE LIST */}
            <div style={styles.fileList}>
              {files.slice(0, 6).map((f, i) => (
                <div key={i} style={styles.fileRow}>
                  <span style={styles.fileIcon}>📄</span>
                  <span style={styles.fileName}>{f.name}</span>
                  <span style={{
                    ...styles.fileStatus,
                    ...(step > i + 1 || progress === 100 ? styles.fileStatusDone : {}),
                  }}>
                    {progress === 100 ? "Done" : step > i ? "Done" : step === i ? "Processing" : "Queued"}
                  </span>
                </div>
              ))}
              {files.length > 6 && (
                <div style={{ ...styles.fileRow, ...styles.fileRowMuted }}>
                  <span style={styles.fileIcon}>⋯</span>
                  <span style={styles.fileName}>+{files.length - 6} more files</span>
                </div>
              )}
            </div>

            <div style={styles.divider} />

            {/* STATUS NOTE */}
            <div style={styles.statusNote}>
              <div style={styles.statusNoteIcon}>🔒</div>
              <div style={styles.statusNoteText}>
                Your files are processed securely and never stored permanently on our servers.
              </div>
            </div>

          </div>

        </div>
      </main>

      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes dotBlink { 0%{opacity:0.2} 50%{opacity:1} 100%{opacity:0.2} }
        .active-dot-1 { animation: dotBlink 1.2s infinite 0s }
        .active-dot-2 { animation: dotBlink 1.2s infinite 0.2s }
        .active-dot-3 { animation: dotBlink 1.2s infinite 0.4s }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────
// STYLES
// ─────────────────────────────────────
const styles = {

  page: {
    minHeight: "100vh",
    backgroundColor: "#f9fafb",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  // ── HEADER ──
  header: {
    position: "fixed",
    top: 0, left: 0, right: 0,
    height: "56px",
    backgroundColor: "#0f172a",
    zIndex: 100,
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    display: "flex",
    alignItems: "center",
  },
  headerScrolled: { boxShadow: "0 1px 0 rgba(255,255,255,0.06)" },
  headerHidden:   { transform: "translateY(-100%)" },
  headerInner: {
    width: "100%", maxWidth: "1400px",
    margin: "0 auto", padding: "0 40px",
    display: "flex", alignItems: "center",
    justifyContent: "space-between", boxSizing: "border-box",
  },
  brand:         { display: "flex", alignItems: "center", gap: "12px" },
  logoWrap: {
    fontSize: "20px", width: "36px", height: "36px",
    backgroundColor: "rgba(255,255,255,0.08)",
    borderRadius: "8px",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  brandTitle:    { fontSize: "14px", fontWeight: "700", color: "#ffffff", letterSpacing: "-0.01em" },
  brandSubtitle: { fontSize: "11px", color: "rgba(255,255,255,0.45)", fontWeight: "400", marginTop: "1px" },
  statusPill: {
    display: "flex", alignItems: "center", gap: "7px",
    padding: "5px 12px",
    backgroundColor: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: "999px",
  },
  statusDot: {
    width: "7px", height: "7px", borderRadius: "50%",
    backgroundColor: "#f59e0b",
    boxShadow: "0 0 0 3px rgba(245,158,11,0.25)",
    animation: "pulse 1.5s ease-in-out infinite",
  },
  statusText: { fontSize: "11px", fontWeight: "600", color: "rgba(255,255,255,0.70)", letterSpacing: "0.02em" },

  // ── MAIN ──
  main: {
    paddingTop: "80px", paddingBottom: "60px",
    paddingLeft: "40px", paddingRight: "40px",
    maxWidth: "1400px", margin: "0 auto",
    boxSizing: "border-box", width: "100%",
  },

  pageHeader:   { marginBottom: "28px" },
  eyebrowRow:   { display: "flex", alignItems: "center", gap: "7px", marginBottom: "10px" },
  eyebrowDot:   { width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#6366f1", boxShadow: "0 0 0 3px rgba(99,102,241,0.15)" },
  eyebrow:      { fontSize: "11px", fontWeight: "700", color: "#4f46e5", letterSpacing: "0.07em", textTransform: "uppercase" },
  eyebrowSep:   { fontSize: "11px", color: "#d1d5db" },
  eyebrowMuted: { fontSize: "11px", fontWeight: "600", color: "#9ca3af", letterSpacing: "0.07em", textTransform: "uppercase" },
  pageTitle:    { fontSize: "18px", fontWeight: "700", color: "#111827", margin: 0, letterSpacing: "-0.01em" },
  pageSubtitle: { fontSize: "13px", color: "#6b7280", margin: "4px 0 0 0", fontWeight: "400" },

  // ── GRID ──
  contentGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr",
    gap: "24px", width: "100%", alignItems: "stretch",
  },

  // ── CARDS ──
  card: {
    backgroundColor: "#ffffff", border: "1px solid #e5e7eb",
    borderRadius: "10px", padding: "24px",
    display: "flex", flexDirection: "column", gap: "20px",
    boxSizing: "border-box", height: "100%",
  },
  infoCard: {
    backgroundColor: "#ffffff", border: "1px solid #e5e7eb",
    borderRadius: "10px", padding: "24px",
    display: "flex", flexDirection: "column", gap: "20px",
    boxSizing: "border-box", height: "100%",
  },

  cardHeader:   { display: "flex", alignItems: "center", gap: "14px" },
  iconWrap: {
    width: "40px", height: "40px", borderRadius: "8px",
    backgroundColor: "#eff6ff",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  cardIcon:    { fontSize: "18px" },
  cardTitle:   { fontSize: "14px", fontWeight: "700", color: "#111827", letterSpacing: "-0.01em" },
  cardSubtitle:{ fontSize: "12px", color: "#9ca3af", marginTop: "2px", fontWeight: "400" },

  divider: { height: "1px", backgroundColor: "#f3f4f6", margin: "0 -24px" },

  // ── PROGRESS ──
  progressSection: { display: "flex", flexDirection: "column", gap: "10px" },
  progressTopRow:  { display: "flex", alignItems: "center", justifyContent: "space-between" },
  progressLabel:   { fontSize: "12px", fontWeight: "600", color: "#374151" },
  progressPct:     { fontSize: "13px", fontWeight: "700", color: "#2563eb", letterSpacing: "-0.01em" },
  progressTrack: {
    height: "6px", backgroundColor: "#f3f4f6",
    borderRadius: "999px", overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: "linear-gradient(90deg, #2563eb 0%, #6366f1 100%)",
    borderRadius: "999px",
    transition: "width 0.3s ease",
  },

  // ── STEPS ──
  stepList:    { display: "flex", flexDirection: "column", gap: "16px" },
  stepItem:    { display: "flex", alignItems: "flex-start", gap: "12px" },
  stepIndicator: {
    width: "26px", height: "26px", borderRadius: "50%",
    backgroundColor: "#f3f4f6", border: "1px solid #e5e7eb",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, marginTop: "1px", transition: "background-color 0.2s, border-color 0.2s",
  },
  stepIndicatorActive: { backgroundColor: "#2563eb", borderColor: "#2563eb" },
  stepIndicatorDone:   { backgroundColor: "#10b981", borderColor: "#10b981" },
  stepContent: { display: "flex", flexDirection: "column", gap: "2px", flex: 1 },
  stepTitle:   { fontSize: "13px", fontWeight: "600", color: "#9ca3af", transition: "color 0.2s" },
  stepTitleActive: { color: "#111827" },
  stepTitleDone:   { color: "#6b7280" },
  stepHint:    { fontSize: "11px", color: "#d1d5db", fontWeight: "400", lineHeight: "1.4" },
  activeDots:  { marginLeft: "2px", letterSpacing: "1px" },
  doneTick:    { fontSize: "12px", color: "#10b981", fontWeight: "700", flexShrink: 0, marginTop: "4px" },

  // ── FILE LIST ──
  fileList: { display: "flex", flexDirection: "column", gap: "8px" },
  fileRow: {
    display: "flex", alignItems: "center", gap: "10px",
    padding: "8px 12px",
    backgroundColor: "#fafafa", border: "1px solid #f3f4f6",
    borderRadius: "7px",
  },
  fileRowMuted: { opacity: 0.6 },
  fileIcon:  { fontSize: "13px", flexShrink: 0 },
  fileName: {
    fontSize: "12px", fontWeight: "500", color: "#374151",
    flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  fileStatus: {
    fontSize: "10px", fontWeight: "700", color: "#9ca3af",
    backgroundColor: "#f3f4f6",
    padding: "2px 8px", borderRadius: "999px",
    letterSpacing: "0.04em", textTransform: "uppercase",
    whiteSpace: "nowrap", flexShrink: 0,
  },
  fileStatusDone: { color: "#059669", backgroundColor: "#ecfdf5" },

  // ── STATUS NOTE ──
  statusNote: {
    display: "flex", alignItems: "flex-start", gap: "10px",
    padding: "12px 14px",
    backgroundColor: "#f8faff", border: "1px solid #e0e7ff",
    borderRadius: "8px", marginTop: "auto",
  },
  statusNoteIcon: { fontSize: "14px", flexShrink: 0 },
  statusNoteText: { fontSize: "12px", color: "#6b7280", fontWeight: "400", lineHeight: "1.6" },
};